/* <pdp-quotes> (product testimonials, press highlights via [data-pq-slide]/[data-pq-dot]): one quote at a time, crossfading. Segment buttons pick a quote; the active segment fills
   while autoplay runs; autoplay pauses on hover, focus and when off screen; swipe on touch. All slides share
   one grid cell, so the block is as tall as the longest quote from the first paint (no layout shift); on phones the
   much longer quotes are left out (trim). */
(() => {
  if (customElements.get('pdp-quotes')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const DURATION = 7000;

  class PdpQuotes extends HTMLElement {
    connectedCallback() {
      /* Phones measure the quotes (trim) once the web fonts are in, or the heights are off */
      if (matchMedia('(max-width: 749px)').matches && document.fonts?.ready) document.fonts.ready.then(() => this.setup());
      else this.setup();
    }

    setup() {
      if (this.ready) return;
      this.ready = true;
      this.slides = [...this.querySelectorAll('.pdp-quotes__slide, [data-pq-slide]')];
      this.dots = [...this.querySelectorAll('.pdp-quotes__dot, [data-pq-dot]')];
      if (this.slides.length < 2) return;
      this.trim();
      if (this.slides.length < 2) return;
      this.index = 0;
      this.autoplay = this.dataset.autoplay !== 'false' && !reduce.matches;
      this.style.setProperty('--pq-duration', `${DURATION}ms`);
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.go(i); this.restart(); }));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const i = (this.index + (e.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length;
          this.go(i); this.dots[i]?.focus(); this.restart();
        }
      });
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) { this.go((this.index + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length); this.restart(); }
      });
      if (!this.autoplay) { this.classList.add('is-static'); return; }
      const pause = () => this.stop();
      const resume = () => { if (!this.matches(':hover') && !this.contains(document.activeElement)) this.restart(); };
      this.addEventListener('pointerenter', pause);
      this.addEventListener('pointerleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', () => setTimeout(resume));
      new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.visible ? resume() : this.stop(); }, { threshold: 0.4 }).observe(this);
    }

    /* Phones: the block is as tall as its longest quote, so one long review (or a long translation) left a big empty
     * space under the short ones. Leave out the quotes much taller than the typical one (at least two stay). */
    trim() {
      if (!matchMedia('(max-width: 749px)').matches) return;
      const h = this.slides.map((s) => s.offsetHeight);
      const sorted = [...h].sort((a, b) => a - b);
      const limit = Math.max(sorted[Math.floor((sorted.length - 1) / 2)] * 1.25, sorted[1]);
      const keep = this.slides.map((_, i) => h[i] <= limit);
      if (keep.filter(Boolean).length < 2) return;
      this.slides.forEach((s, i) => { if (!keep[i]) { s.hidden = true; s.classList.remove('is-active'); if (this.dots[i]) this.dots[i].hidden = true; } });
      this.slides = this.slides.filter((_, i) => keep[i]);
      this.dots = this.dots.filter((_, i) => keep[i]);
      this.slides.forEach((s, i) => { s.classList.toggle('is-active', i === 0); i === 0 ? s.removeAttribute('aria-hidden') : s.setAttribute('aria-hidden', 'true'); });
      this.dots.forEach((d, i) => (i === 0 ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    }

    go(i) {
      if (i === this.index) return;
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.dots[this.index]?.removeAttribute('aria-current');
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.slides[i].removeAttribute('aria-hidden');
      this.dots[i]?.setAttribute('aria-current', 'true');
    }

    stop() {
      clearTimeout(this.timer);
      this.classList.remove('is-playing');
    }

    restart() {
      this.stop();
      if (!this.autoplay || this.visible === false) return;
      void this.offsetWidth; // restart the segment fill animation
      this.classList.add('is-playing');
      this.timer = setTimeout(() => { this.go((this.index + 1) % this.slides.length); this.restart(); }, DURATION);
    }
  }

  if (!customElements.get('pdp-quotes')) customElements.define('pdp-quotes', PdpQuotes);
})();
